import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import ArticleCard from "../components/ArticleCard";
import Newsletter from "../components/Newsletter";
import ArchiveWidget from "../components/ArchiveWidget";
import { ArrowRight, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import SEO from "../components/SEO";

const HomePage = () => {
  const [featuredArticles, setFeaturedArticles] = useState([]);
  const [latestArticles, setLatestArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Seed database if needed
        await axios.post(`${API}/seed`).catch(() => {});
        
        // Fetch featured articles
        const featuredRes = await axios.get(`${API}/articles?featured=true&limit=3`);
        setFeaturedArticles(featuredRes.data.articles);
        
        // Fetch latest articles
        const latestRes = await axios.get(`${API}/articles?limit=6`);
        setLatestArticles(latestRes.data.articles);
      } catch (error) {
        console.error("Failed to fetch articles:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-16">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="pt-16" data-testid="home-page">
      <SEO url="/" />
      {/* Hero Section with Cover Image */}
      <section className="relative min-h-[70vh] md:min-h-[80vh] flex items-center justify-center overflow-hidden">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://customer-assets.emergentagent.com/job_rebel-mag/artifacts/wzfh3b99_1000032541.png')`
          }}
        />
        {/* Overlay - balanced for text contrast while showing image */}
        <div className="absolute inset-0 bg-background/50" />
        
        {/* Content */}
        <div className="relative z-10 text-center px-4 max-w-4xl mx-auto">
          <img 
            src="https://customer-assets.emergentagent.com/job_rebel-mag/artifacts/b4hgwle1_1000032534-removebg-preview.png" 
            alt="Mancunia Rebél Est. 2014" 
            className="h-32 md:h-48 w-auto mx-auto mb-8 drop-shadow-2xl brightness-0 invert"
          />
          <h1 className="font-heading font-black text-4xl md:text-6xl lg:text-7xl uppercase tracking-tighter leading-none mb-4 border-2 border-primary inline-block px-6 py-4">
            Empower Creatives
          </h1>
          <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto mb-8">
            Celebrating media, independent music, fashion, the arts, film, photography & independent creatives worldwide.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/articles">
              <Button 
                className="bg-primary text-primary-foreground hover:bg-primary/90 h-12 px-8 font-mono uppercase tracking-widest text-xs w-full sm:w-auto"
                data-testid="hero-explore-btn"
              >
                Explore Articles
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
            <Link to="/about">
              <Button 
                variant="outline"
                className="bg-transparent border border-border text-foreground hover:bg-white hover:text-black h-12 px-8 font-mono uppercase tracking-widest text-xs w-full sm:w-auto"
                data-testid="hero-about-btn"
              >
                Our Mission
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Featured Articles Bento Grid */}
      <section className="py-8 md:py-12 border-t border-border">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          {featuredArticles.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-6">
              {/* Main Featured Article */}
              <div className="md:col-span-8 md:row-span-2">
                <ArticleCard article={featuredArticles[0]} variant="hero" />
              </div>
              
              {/* Secondary Featured Articles */}
              {featuredArticles.slice(1, 3).map((article) => (
                <div key={article.article_id} className="md:col-span-4">
                  <ArticleCard article={article} variant="featured" />
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Latest Articles Section */}
      <section className="py-24 md:py-32 border-t border-border">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between mb-12">
            <div>
              <span className="font-mono text-xs uppercase tracking-widest text-primary">Latest</span>
              <h2 className="font-heading font-black text-4xl md:text-5xl uppercase tracking-tighter mt-2">
                Fresh Off The Streets
              </h2>
            </div>
            <Link to="/articles">
              <Button 
                variant="outline"
                className="bg-transparent border border-border text-foreground hover:bg-white hover:text-black h-12 px-8 font-mono uppercase tracking-widest text-xs hidden md:flex"
                data-testid="home-view-all"
              >
                View All
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-16">
            {latestArticles.map((article) => (
              <ArticleCard key={article.article_id} article={article} />
            ))}
          </div>

          <div className="mt-12 md:hidden">
            <Link to="/articles">
              <Button 
                variant="outline"
                className="w-full bg-transparent border border-border text-foreground hover:bg-white hover:text-black h-12 px-8 font-mono uppercase tracking-widest text-xs"
                data-testid="home-view-all-mobile"
              >
                View All Articles
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* From the Archive (random Tumblr legacy articles) */}
      <ArchiveWidget />

      {/* About Teaser */}
      <section className="py-24 md:py-32 bg-card border-y border-border">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="font-mono text-xs uppercase tracking-widest text-primary">Mission</span>
              <h2 className="font-heading font-black text-4xl md:text-6xl uppercase tracking-tighter mt-2 leading-none">
                Empower Creatives. Celebrate Media.
              </h2>
            </div>
            <div>
              <p className="text-muted-foreground text-lg leading-relaxed mb-8">
                Mancunia Rebél celebrates media, independent music across all genres, fashion, the arts, film, photography, philanthropy, technology, models & independent creatives. Our goal is to develop a company that empowers creatives.
              </p>
              <Link to="/about">
                <Button 
                  className="bg-primary text-primary-foreground hover:bg-primary/90 h-12 px-8 font-mono uppercase tracking-widest text-xs btn-track"
                  data-testid="home-learn-more"
                >
                  Learn More
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Newsletter */}
      <Newsletter />
    </div>
  );
};

export default HomePage;
